"use client";
/**
 * One line of a bounded set, as four declared columns rather than a dozen
 * independent flex negotiations.
 *
 *   44 media · identity 1fr · energy ≤96 · open hint
 *
 * with a DECLARED truncation order of `time → yield → kind → brand`. The
 * serving basis is deliberately absent from that order and never drops: it is
 * the fact the energy figure beside it is meaningless without, and the captured
 * rows genuinely disagree about it — `Per 101g`, `Per 100g` and `Per 4 oz` in
 * one three-row set. The title yields last, because on a page where all three
 * rows are called some form of `Chicken Breast` the name plus its brand is the
 * only thing telling them apart.
 *
 * THE ROW DOES NOT READ THE SERVING-SCOPE AXIS, and that is a width law rather
 * than a preference. The scope line lives UNDER the energy figure, in a column
 * capped at 96 pixels — and the longest real serving description is
 * `1 oz, with bone cooked (yield after bone removed)`, 49 characters, which is
 * 53 with its `per` prefix. Fifty-three characters under a 96-pixel figure is
 * either seven wrapped lines or a clip. So the row states the basis in its meta
 * line, where the identity zone is ~350 pixels at Wide and the fact line
 * measures itself, and the scope axis moves only the headline surfaces that
 * have room for it.
 *
 * The 44-pixel lead is a recognition cue and is inert. Half the rows on this
 * pillar have no picture at all — the food endpoint publishes none — so the
 * lead is a designed absence on those, which is why it is small enough not to
 * dominate a row it cannot fill.
 */
import type { ReactElement } from "react";
import {
  CresultcardIdentity,
  CresultcardRow,
  CresultcardFigureScale,
  CresultcardIdentitySlot,
  CresultcardMediaScale,
  CresultcardRowIdentityFloor,
  CresultcardTitleLines,
} from "@/components/ui/Cresultcard";
import {
  C_NUTRITION_ROW_ENERGY_MAX,
  C_NUTRITION_ROW_MEDIA,
  C_NUTRITION_ROW_VARIANT,
} from "./cnutritioncard.constants";
import {
  CnutritioncardEnergy,
  CnutritioncardMetaLine,
  CnutritioncardShot,
} from "./CnutritioncardMeta";
import {
  CnutritioncardServingScope,
  type CnutritioncardRowProps,
} from "./cnutritioncard.types";

/** The title yields last, and only after the meta line has dropped its own. */
const ROW_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

export function CnutritioncardRow({
  data,
  onLast = false,
  openControl,
}: CnutritioncardRowProps): ReactElement {
  return (
    <CresultcardRow
      figure={
        <span
          style={{
            display: "inline-flex",
            justifyContent: "flex-end",
            maxWidth: C_NUTRITION_ROW_ENERGY_MAX,
            minWidth: 0,
            flex: "0 1 auto",
          }}
        >
          <CnutritioncardEnergy
            data={data}
            scale={CresultcardFigureScale.Row}
            servingScope={CnutritioncardServingScope.FrameOnly}
          />
        </span>
      }
      identity={
        // `/Identity/Block` rather than `/Identity`: the kit's row already
        // stamps its own identity ZONE with that path.
        <CresultcardIdentity
          openControl={openControl}
          title={data.name}
          titleLines={CresultcardTitleLines.Two}
          truncationOrder={ROW_TRUNCATION}
        />
      }
      identityFloor={CresultcardRowIdentityFloor.Title}
      lead={
        <span
          style={{
            display: "block",
            width: C_NUTRITION_ROW_MEDIA,
            flex: "0 0 auto",
          }}
        >
          <CnutritioncardShot
            data={data}
            scale={CresultcardMediaScale.Thumb}
          />
        </span>
      }
      meta={
        <CnutritioncardMetaLine
          data={data}
        />
      }
      onLast={onLast}
      openHint
      variant={C_NUTRITION_ROW_VARIANT}
    />
  );
}
